import React from "react";
import { Helmet } from "react-helmet-async";

import {
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  FaArrowRight,
  FaLightbulb,
  FaBullseye,
  FaWhatsapp,
} from "react-icons/fa";

const About = () => {
  const whatsappLink =
    "https://wa.me/919082280240?text=Hi%20Option%20Bricks,%20I'm%20interested%20in%20your%20market%20insights.";

  return (
    <section className="bg-white text-black font-sans overflow-hidden">


     <Helmet>
        <title>About Us | Option Bricks</title>
        <meta
          name="description"
          content="Learn how Option Bricks delivers data-backed research across Nifty, BankNifty, and Sensex to help traders make informed investment decisions."
        />
      </Helmet>


      {/* --- HERO SECTION --- */}
      <div className="relative w-full py-16 lg:py-24 px-6 lg:px-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="w-full lg:w--full animate-fadeInLeft">
              <div className="inline-block px-4 py-1 rounded-full border border-[#1e6091]/20 bg-[#1e6091]/[0.06] text-[#1e6091] text-xs font-semibold tracking-widest uppercase mb-4">
                About Me
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold mb-6 leading-tight tracking-tight text-black">
                What{" "}
                <span className="text-[#1e6091]">
                  Option Bricks
                </span>{" "}
                Do
              </h1>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-6">
                Research-Driven Investment Insights
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We deliver data-backed research across Nifty, BankNifty, and
                Sensex to help traders stay ahead of the curve.
              </p>

              <div className="flex flex-nowrap lg:w-1/2 gap-3 mt-6">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full md:w-1/2 bg-[#1e6091] text-white font-medium py-3 rounded-full shadow-sm hover:bg-[#185076] hover:shadow-md transition-all text-sm md:text-base active:scale-95"
                >
                  <FaWhatsapp className="text-lg" />
                  Chat on WhatsApp
                </a>

                {/* <button className="w-1/2 border border-gray-300 text-black font-medium py-3 rounded-full hover:border-black hover:shadow-md transition-all text-sm md:text-base">
                  Learn More
                </button> */}
              </div>
            </div>

            {/* <div className="w-full lg:w-1/2 animate-fadeInRight">
              <div className="relative p-2 bg-white rounded-3xl shadow-2xl shadow-[#1e6091]/10 border border-gray-100">
                <video className="rounded-2xl w-full aspect-video object-cover" controls autoPlay loop muted>
                  <source src={require("../../img/7579564-uhd_4096_2160_25fps.mp4")} type="video/mp4" />
                </video>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* --- CORE METHODOLOGY --- */}
      <div className="w-full py-24 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {/* Card 1: Market Analysis */}
            <div className="group relative bg-white p-10 rounded-3xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-2xl transition-shadow duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-8 group-hover:bg-white group-hover:scale-110 transition-all duration-500">
                  <FaChartLine className="text-black text-3xl group-hover:!text-[#1e6091] transition-colors duration-500" />
                </div>
                <h3 className="text-2xl font-semibold text-black group-hover:!text-white transition-colors duration-500 mb-4 tracking-tight">
                  Market Analysis & Strategy
                </h3>
                <p className="text-gray-500 group-hover:text-white/80 transition-colors duration-500 text-base leading-relaxed">
                  We provide well-researched market views, including analysis of
                  trends, sectors, and macro-economic factors to support
                  informed investment decisions.
                </p>
              </div>
            </div>

            {/* Card 2: Research Insights */}
            <div className="group relative bg-white p-10 rounded-3xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-2xl transition-shadow duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-8 group-hover:bg-white group-hover:scale-110 transition-all duration-500">
                  <FaLightbulb className="text-black text-3xl group-hover:!text-[#1e6091] transition-colors duration-500" />
                </div>
                <h3 className="text-2xl font-semibold text-black group-hover:!text-white transition-colors duration-500 mb-4 tracking-tight">
                  Research-Based Insights
                </h3>
                <p className="text-gray-500 group-hover:text-white/80 transition-colors duration-500 text-base leading-relaxed">
                  Our recommendations are derived from a structured approach
                  combining technical and fundamental analysis. These insights
                  are intended to assist investors and do not guarantee returns.
                </p>
              </div>
            </div>

            {/* Card 3: Periodic Reports */}
            <div className="group relative bg-white p-10 rounded-3xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-2xl transition-shadow duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-8 group-hover:bg-white group-hover:scale-110 transition-all duration-500">
                  <FaBullseye className="text-black text-3xl group-hover:!text-[#1e6091] transition-colors duration-500" />
                </div>
                <h3 className="text-2xl font-semibold text-black group-hover:!text-white transition-colors duration-500 mb-4 tracking-tight">
                  Periodic Research Reports
                </h3>
                <p className="text-gray-500 group-hover:text-white/80 transition-colors duration-500 text-base leading-relaxed">
                  We publish detailed research reports on a regular basis to
                  help clients understand market developments and investment
                  opportunities.
                </p>
              </div>
            </div>

            {/* Card 4: Communication (WhatsApp Linked) */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-white p-10 rounded-3xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-2xl transition-shadow duration-500 overflow-hidden block z-10"
            >
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-8 group-hover:bg-white group-hover:scale-110 group-hover:-rotate-12 transition-all duration-500">
                  <FaWhatsapp className="text-black text-3xl group-hover:!text-[#1e6091] transition-colors duration-500" />
                </div>
                <h3 className="text-2xl font-semibold text-black group-hover:!text-white transition-colors duration-500 mb-4 tracking-tight">
                  Timely Communication
                </h3>
                <p className="text-gray-500 group-hover:text-white/80 transition-colors duration-500 text-base leading-relaxed mb-6">
                  Research updates and insights are shared through convenient
                  channels such as SMS, WhatsApp, or email for ease of access.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-black group-hover:text-white uppercase tracking-wide group-hover:translate-x-2 transition-all duration-300">
                  Click to Join WhatsApp &rarr;
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* --- WHY CHOOSE US --- */}
      <div className="w-full py-24 px-6 lg:px-16 bg-white font-sans">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-16 text-black tracking-tight">
            Reliable Stock Insights for Smarter Trading
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1: Data-Driven */}
            <div className="group relative p-10 md:p-12 rounded-[2rem] bg-zinc-50 border border-zinc-200 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-white group-hover:bg-white border border-zinc-200 group-hover:border-white rounded-2xl flex items-center justify-center shadow-sm mb-6 transition-colors duration-500 group-hover:scale-110 transform">
                  <FaChartLine className="text-black group-hover:!text-[#1e6091] text-3xl transition-colors duration-500" />
                </div>
                <h3 className="font-bold text-2xl text-black group-hover:!text-white transition-colors duration-500 mb-3 tracking-tight">
                  Data-Driven
                </h3>
                <p className="text-zinc-500 group-hover:text-zinc-300 transition-colors duration-500 text-base font-medium">
                  Professional analysis you can trust.
                </p>
              </div>
            </div>

            {/* Feature 2: Secure Platform */}
            <div className="group relative p-10 md:p-12 rounded-[2rem] bg-zinc-50 border border-zinc-200 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-white group-hover:bg-white border border-zinc-200 group-hover:border-white rounded-2xl flex items-center justify-center shadow-sm mb-6 transition-colors duration-500 group-hover:scale-110 transform">
                  <FaShieldAlt className="text-black group-hover:!text-[#1e6091] text-3xl transition-colors duration-500" />
                </div>
                <h3 className="font-bold text-2xl text-black group-hover:!text-white transition-colors duration-500 mb-3 tracking-tight">
                  Secure Platform
                </h3>
                <p className="text-zinc-500 group-hover:text-zinc-300 transition-colors duration-500 text-base font-medium">
                  Reliable and easy-to-use trading tools.
                </p>
              </div>
            </div>

            {/* Feature 3: Community */}
            <div className="group relative p-10 md:p-12 rounded-[2rem] bg-zinc-50 border border-zinc-200 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-default z-10">
              {/* Top-to-Bottom Animated Background Layer */}
              <div className="absolute inset-0 bg-[#1e6091] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-in-out z-[-1]"></div>

              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-white group-hover:bg-white border border-zinc-200 group-hover:border-white rounded-2xl flex items-center justify-center shadow-sm mb-6 transition-colors duration-500 group-hover:scale-110 transform">
                  <FaUsers className="text-black group-hover:!text-[#1e6091] text-3xl transition-colors duration-500" />
                </div>
                <h3 className="font-bold text-2xl text-black group-hover:!text-white transition-colors duration-500 mb-3 tracking-tight">
                  Community
                </h3>
                <p className="text-zinc-500 group-hover:text-zinc-300 transition-colors duration-500 text-base font-medium">
                  Grow with a network of smart traders.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInLeft {
          animation: fadeInLeft 0.8s ease-out forwards;
        }
        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }
        .moving-border-card::before {
          content: "";
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(
            transparent,
            #43a9a8,
            transparent,
            #1e6091,
            transparent
          );
          animation: rotate 6s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default About;
